import { Address, base16, BCS, ed25519, HDPrivKey, OpCode, Pointer, PrivKey, ref, Tx, util, } from '@aldea/core';
import { AbiQuery, assertClassLike, assertProxy, } from '@aldea/core/abi';
import { CallInstruction, DeployInstruction, ExecInstruction, FundInstruction, ImportInstruction, LoadByOriginInstruction, LoadInstruction, LockInstruction, NewInstruction, SignInstruction, SignToInstruction, } from '@aldea/core/instructions';
/**
 * Transaction Builder
 *
 * A simple API for building transactions.
 */
export class TxBuilder {
    aldea;
    buildSteps = new Array();
    buildHooks = new Array();
    afterHooks = new Array();
    results = new Array();
    isBuilding = false;
    _tx = new Tx();
    constructor(aldea, opts = {}) {
        this.aldea = aldea;
        if (opts.extend) {
            for (let instruction of opts.extend.instructions) {
                this.push(instruction);
            }
        }
        if (opts.onBuild) {
            for (let hook of Array.isArray(opts.onBuild) ? opts.onBuild : [opts.onBuild]) {
                this.buildHooks.push(hook);
            }
        }
        if (opts.afterBuild) {
            for (let hook of Array.isArray(opts.afterBuild) ? opts.afterBuild : [opts.afterBuild]) {
                this.afterHooks.push(hook);
            }
        }
        if (opts.updateSigs) {
            for (let privKey of Array.isArray(opts.updateSigs) ? opts.updateSigs : [opts.updateSigs]) {
                this.afterHooks.push(createSigHook(privKey, opts.extend?.instructions.length));
            }
        }
    }
    /**
     * Builds the transaction. Executes the build steps and returns the Tx.
     */
    async build() {
        this.refuteBuilding('build');
        this.isBuilding = true;
        this._tx = new Tx();
        this.results = [];
        for (let i = 0; i < this.buildSteps.length; i++) {
            const txid = this._tx.id;
            const [step, args] = this.buildSteps[i];
            const instruction = await this.applyHooks(this.buildHooks, step(this, ...args), i);
            this.refuteMutation(txid);
            this._tx.push(instruction);
            this.results[i] = await this.resultFromInstruction(instruction);
        }
        for (let i = 0; i < this._tx.instructions.length; i++) {
            if (this._tx.instructions[i].opcode === OpCode.SIGN) {
                const inst = this._tx.instructions[i];
                const [privKey] = this.buildSteps[i][1];
                if ((privKey instanceof PrivKey || privKey instanceof HDPrivKey) && inst.sig.every(b => b === 0)) {
                    this._tx.instructions[i] = TxBuilder.signInstruction(this, privKey);
                }
            }
            this._tx.instructions[i] = await this.applyHooks(this.afterHooks, this._tx.instructions[i], i);
        }
        this.isBuilding = false;
        return this._tx;
    }
    /**
     * Gets an InstructionResult from the stack of results.
     */
    getResult(ref, type) {
        const res = this.results[ref.idx];
        if (!res) {
            throw new Error(`intruction result not found: ${ref.idx}`);
        }
        else if (res.type !== type) {
            const expected = ResultType[type];
            const actual = ResultType[res.type];
            throw new Error(`wrong instruction result: ${expected} expected, got ${actual}`);
        }
        return res;
    }
    /**
     * Returns the transaction being built. Can only be accessed during building.
     */
    get tx() {
        if (!this.isBuilding)
            throw new Error('TxBuilder.tx can only be accessed during build');
        return this._tx;
    }
    /**
     * Pushes an IMPORT instruction onto the Transaction.
     */
    import(pkgId) {
        if (typeof pkgId === 'string')
            pkgId = base16.decode(pkgId);
        return this.push(TxBuilder.importInstruction, pkgId);
    }
    /**
     * Pushes a LOAD instruction onto the Transaction.
     */
    load(outputId) {
        if (typeof outputId === 'string')
            outputId = base16.decode(outputId);
        return this.push(TxBuilder.loadInstruction, outputId);
    }
    /**
     * Pushes a LOADBYORIGIN instruction onto the Transaction.
     */
    loadByOrigin(origin) {
        if (typeof origin === 'string')
            origin = Pointer.fromString(origin);
        return this.push(TxBuilder.loadByOriginInstruction, origin);
    }
    /**
     * Pushes a NEW instruction onto the Transaction. Accepts an InstructionRef
     * (which must refer to a PkgResult), a class name and a list of args.
     */
    new(ref, className, args = []) {
        return this.push(TxBuilder.newInstruction, ref, className, args);
    }
    /**
     * Pushes a CALL instruction onto the Transaction. Accepts an InstructionRef
     * (which must refer to a JigResult), a method name and a list of args.
     */
    call(ref, methodName, args = []) {
        return this.push(TxBuilder.callInstruction, ref, methodName, args);
    }
    /**
     * Pushes an EXEC instruction onto the Transaction. Accepts an InstructionRef
     * (which must refer to a PkgResult), a function name and a list of args.
     */
    exec(ref, functionName, args = []) {
        return this.push(TxBuilder.execInstruction, ref, functionName, args);
    }
    /**
     * Pushes a FUND instruction onto the Transaction. Accepts an InstructionRef
     * (which must refer to a JigResult).
     */
    fund(ref) {
        return this.push(TxBuilder.fundInstruction, ref);
    }
    /**
     * Pushes a LOCK instruction onto the Transaction. Accepts an InstructionRef
     * (which must refer to a JigResult), and an address instance of string.
     */
    lock(ref, address) {
        if (typeof address === 'string')
            address = Address.fromString(address);
        return this.push(TxBuilder.lockInstruction, ref, address);
    }
    deploy(entryOrCode, code) {
        let entry;
        let pkg;
        if (code instanceof Map) {
            entry = Array.isArray(entryOrCode) ? entryOrCode : [entryOrCode];
            pkg = code;
        }
        else if (entryOrCode instanceof Map) {
            entry = Array.from(entryOrCode.keys());
            pkg = entryOrCode;
        }
        else {
            throw new Error('invalid deploy params');
        }
        return this.push(TxBuilder.deployInstruction, entry, pkg);
    }
    /**
     * Pushes a SIGN instruction onto the Transaction. The given PrivKey is used
     * to create the signature used in the instruction.
     */
    sign(privKey) {
        return this.push(TxBuilder.signInstruction, privKey, true);
    }
    /**
     * Pushes a SIGNTO instruction onto the Transaction. The given PrivKey is used
     * to create the signature used in the instruction.
     */
    signTo(privKey) {
        return this.push(TxBuilder.signToInstruction, privKey);
    }
    /**
     * Pushes a build step onto the stack of steps. The build step must return an
     * instruction.
     */
    push(instruction, ...args) {
        this.refuteBuilding('push');
        const step = typeof instruction === 'function' ? instruction : () => instruction;
        const len = this.buildSteps.push([step, args]);
        return ref(len - 1);
    }
    // Applies the list of hooks on the given instruction.
    async applyHooks(hooks, instruction, idx) {
        return hooks.reduce(async (instP, hook) => {
            const inst = await instP;
            const repl = await hook(this, inst, idx);
            if (typeof repl?.opcode === 'number') {
                if (repl.opcode !== inst.opcode) {
                    throw new Error(`TxBuilder hook instruction mismatch. ${OpCode[inst.opcode]} expected, ${OpCode[repl.opcode]} received.`);
                }
                return repl;
            }
            return inst;
        }, instruction);
    }
    // Returns the instruction result for the given instruction.
    async resultFromInstruction(instruction) {
        switch (instruction.opcode) {
            case OpCode.IMPORT: {
                const i = instruction;
                const pkgId = base16.encode(i.pkgId);
                const abi = await this.aldea.getPackageAbi(pkgId);
                return pkgResult(abi);
            }
            case OpCode.LOAD: {
                const i = instruction;
                const outputId = base16.encode(i.outputId);
                const output = await this.aldea.getOutput(outputId);
                const pkgPtr = Pointer.fromString(output.class);
                const abi = await this.aldea.getPackageAbi(pkgPtr.id);
                return jigResult(abi, pkgPtr.idx);
            }
            case OpCode.LOADBYORIGIN: {
                const i = instruction;
                const origin = Pointer.fromBytes(i.origin);
                const output = await this.aldea.getOutputByOrigin(origin.toString());
                const pkgPtr = Pointer.fromString(output.class);
                const abi = await this.aldea.getPackageAbi(pkgPtr.id);
                return jigResult(abi, pkgPtr.idx);
            }
            case OpCode.NEW: {
                const i = instruction;
                const res = this.getResult(ref(i.idx), ResultType.PKG);
                return jigResult(res.abi, i.exportIdx);
            }
            case OpCode.CALL: {
                const i = instruction;
                const res = this.getResult(ref(i.idx), ResultType.JIG);
                const method = new AbiQuery(res.abi).fromExports().byIndex(res.exportIdx).getMethod(i.methodIdx);
                return this.resultFromReturnType(res.abi, method.rtype);
            }
            case OpCode.EXEC: {
                const i = instruction;
                const res = this.getResult(ref(i.idx), ResultType.PKG);
                const func = new AbiQuery(res.abi).fromExports().byIndex(i.exportIdx).getFunction();
                return this.resultFromReturnType(res.abi, func.rtype);
            }
            case OpCode.FUND:
                return noResult();
            case OpCode.LOCK:
                return noResult();
            case OpCode.DEPLOY:
                // todo - can return a PkgResult if we compile the code here and get the ABI?
                return noResult();
            case OpCode.SIGN:
                return noResult();
            case OpCode.SIGNTO:
                return noResult();
            default:
                throw new Error(`unrecognized instruction opcode: ${instruction.opcode}`);
        }
    }
    // Returns a typed instruction result from a method/function call
    async resultFromReturnType(abi, rtype) {
        if (rtype) {
            let exported;
            let imported;
            //let interfaceNode: InterfaceNode | undefined
            const query = new AbiQuery(abi);
            if (rtype.name === 'Coin') {
                const coinAbi = await this.aldea.getPackageAbi('0000000000000000000000000000000000000000000000000000000000000000');
                const coinQuery = new AbiQuery(coinAbi);
                const exportIdx = coinQuery.fromExports().byName('Coin').getIndex();
                return jigResult(coinAbi, exportIdx);
            }
            if (query.fromExports().byName(rtype.name).hasResult) {
                exported = query.getCode();
                assertClassLike(exported);
                const exportIdx = query.getIndex();
                return jigResult(abi, exportIdx);
            }
            if (query.fromImports().byName(rtype.name).hasResult) {
                imported = query.getCode();
                assertProxy(imported);
                const remoteAbi = await this.aldea.getPackageAbi(imported.pkg);
                const exportIdx = new AbiQuery(remoteAbi).fromExports().byName(imported.name).getIndex();
                return jigResult(remoteAbi, exportIdx);
            }
        }
        // todo - can return a "typed result" when we know the type
        return noResult();
    }
    // Throws if the TxBuilder is currently building
    refuteBuilding(name) {
        if (this.isBuilding) {
            name = name ? `TxBuilder.${name}()` : 'TxBuilder method';
            throw new Error(`cannot call ${name} whilst is building`);
        }
    }
    // Throws if the txid does not match the tx being built
    refuteMutation(txid) {
        if (this.tx.id !== txid) {
            throw new Error('cannot mutate the Tx object whilst TxBuilder is building');
        }
    }
}
/**
 * The TxBuilder namespace also exports a number of static functions for
 * creating individual instructions. These are used internally by the TxBuilder
 * but exposed as can be useful when overriding and customising the TxBuilder.
 */
(function (TxBuilder) {
    /**
     * Creates and returns an IMPORT instruction.
     */
    function importInstruction(_txb, pkgId) {
        return new ImportInstruction(pkgId);
    }
    TxBuilder.importInstruction = importInstruction;
    /**
     * Creates and returns a LOAD instruction.
     */
    function loadInstruction(_txb, outputId) {
        return new LoadInstruction(outputId);
    }
    TxBuilder.loadInstruction = loadInstruction;
    /**
     * Creates and returns a LOADBYORIGIN instruction.
     */
    function loadByOriginInstruction(_txb, origin) {
        return new LoadByOriginInstruction(origin.toBytes());
    }
    TxBuilder.loadByOriginInstruction = loadByOriginInstruction;
    /**
     * Creates and returns a NEW instruction.
     */
    function newInstruction(txb, ref, className, args) {
        const res = txb.getResult(ref, ResultType.PKG);
        const exportIdx = new AbiQuery(res.abi).fromExports().byName(className).getIndex();
        const argsBuf = new BCS(res.abi).encode(`${className}_constructor`, args);
        return new NewInstruction(ref.idx, exportIdx, argsBuf);
    }
    TxBuilder.newInstruction = newInstruction;
    /**
     * Creates and returns a CALL instruction.
     */
    function callInstruction(txb, ref, methodName, args) {
        const res = txb.getResult(ref, ResultType.JIG);
        const query = new AbiQuery(res.abi).fromExports().byIndex(res.exportIdx);
        const klass = query.getCode();
        assertClassLike(klass);
        const methodIdx = query.getMethodIdx(methodName);
        const argsBuf = new BCS(res.abi).encode(`${klass.name}_${methodName}`, args);
        return new CallInstruction(ref.idx, methodIdx, argsBuf);
    }
    TxBuilder.callInstruction = callInstruction;
    /**
     * Creates and returns an EXEC instruction.
     */
    function execInstruction(txb, ref, functionName, args) {
        const res = txb.getResult(ref, ResultType.PKG);
        const funcIdx = new AbiQuery(res.abi).fromExports().byName(functionName).getIndex();
        const argsBuf = new BCS(res.abi).encode(functionName, args);
        return new ExecInstruction(ref.idx, funcIdx, argsBuf);
    }
    TxBuilder.execInstruction = execInstruction;
    /**
     * Creates and returns a FUND instruction.
     */
    function fundInstruction(txb, ref) {
        txb.getResult(ref, ResultType.JIG); // pull the index to check type
        return new FundInstruction(ref.idx);
    }
    TxBuilder.fundInstruction = fundInstruction;
    /**
     * Creates and returns a LOCK instruction.
     */
    function lockInstruction(txb, ref, address) {
        txb.getResult(ref, ResultType.JIG); // pull the index to check type
        return new LockInstruction(ref.idx, address.hash);
    }
    TxBuilder.lockInstruction = lockInstruction;
    /**
     * Creates and returns a DEPLOY instruction.
     */
    function deployInstruction(_txb, entry, code) {
        const pkg = BCS.pkg.encode([entry, code]);
        return new DeployInstruction(pkg);
    }
    TxBuilder.deployInstruction = deployInstruction;
    /**
     * Creates and returns a SIGN instruction.
     *
     * The last arg allows the signature to be fakes with 64 empty bytes. The
     * TxBuilder will fake the signature on first pass, and when the entire TX is
     * built will re-sign and blanked sigs.
     */
    function signInstruction(txb, privKey, fakeIt = false) {
        let sig;
        if (fakeIt) {
            sig = new Uint8Array(64);
        }
        else {
            const msg = txb.tx.sighash();
            sig = ed25519.sign(msg, privKey);
        }
        return new SignInstruction(sig, privKey.toPubKey().toBytes());
    }
    TxBuilder.signInstruction = signInstruction;
    /**
     * Creates and returns a SIGNTO instruction.
     */
    function signToInstruction(txb, privKey) {
        const msg = txb.tx.sighash(txb.tx.instructions.length);
        const sig = ed25519.sign(msg, privKey);
        return new SignToInstruction(sig, privKey.toPubKey().toBytes());
    }
    TxBuilder.signToInstruction = signToInstruction;
})(TxBuilder || (TxBuilder = {}));
// Helper for generating a hook function that will resign any signatures that
// match the given private key.
// Only matches SIGN instructions as it should be unnecessary to resign SIGNTO. 
function createSigHook(privKey, len) {
    const pubKey = privKey.toPubKey();
    return function sigHook(txb, instruction, idx) {
        if (instruction.opcode === OpCode.SIGN &&
            util.buffEquals(instruction.pubkey, pubKey.toBytes()) &&
            (typeof len === 'undefined' || idx < len)) {
            return TxBuilder.signInstruction(txb, privKey);
        }
    };
}
// InstructionResult type
var ResultType;
(function (ResultType) {
    ResultType[ResultType["NONE"] = 0] = "NONE";
    ResultType[ResultType["PKG"] = 1] = "PKG";
    ResultType[ResultType["JIG"] = 2] = "JIG";
})(ResultType || (ResultType = {}));
// generates a NoResult
function noResult() {
    return { type: ResultType.NONE };
}
// generates a PkgResult
function pkgResult(abi) {
    return { type: ResultType.PKG, abi };
}
// generates a JigResult
function jigResult(abi, exportIdx) {
    return { type: ResultType.JIG, abi, exportIdx };
}
function isFakedSignInstruction(instruction) {
    return instruction.opcode === OpCode.SIGN && instruction.sig.every(b => b === 0);
}
